import { GenerateDocumentResponse, GenerateQuittanceDto } from "@dune/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { privateAxios } from "../axios";
import { propositionsQueryKeys } from "../query-keys/propositions.query-keys";

const generateQuittanceDocument = async (
	propositionId: number,
	receiptId: string,
	data: GenerateQuittanceDto,
): Promise<GenerateDocumentResponse> => {
	const { data: response } = await privateAxios.post<GenerateDocumentResponse>(
		`/propositions/${propositionId}/generate-receipt`,
		{ receiptId, ...data },
	);
	return response;
};

export const useGenerateQuittance = (
	propositionId: number,
	receiptId: string,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: GenerateQuittanceDto) =>
			generateQuittanceDocument(propositionId, receiptId, data),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.detail(propositionId),
					refetchType: "active",
				}),
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.documents(propositionId),
					refetchType: "active",
				}),
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.tasks(propositionId),
					refetchType: "active",
				}),
			]);
		},
	});
};

export const useGenerateQuittanceWithReceiptId = (propositionId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: GenerateQuittanceDto & { receiptId: string }) =>
			generateQuittanceDocument(propositionId, data.receiptId, {
				templateId: data.templateId,
				signataireDuneId: data.signataireDuneId,
				format: data.format,
				skipTaskValidation: data.skipTaskValidation,
			}),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.detail(propositionId),
					refetchType: "active",
				}),
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.documents(propositionId),
					refetchType: "active",
				}),
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.tasks(propositionId),
					refetchType: "active",
				}),
			]);
		},
	});
};
